import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useMutation, useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import { Alert, Button, Step, Stepper } from '@freelancelabs/components';

import RefusalNotice from '../../../components/RefusalNotice';
import { InviteUserDocument, RolesDocument } from '../../../graphql/__generated__/types';
import AttachmentsStep from './AttachmentsStep';
import DashboardsStep from './DashboardsStep';
import IdentityStep from './IdentityStep';
import PartnerListStep from './PartnerListStep';
import ReviewStep from './ReviewStep';
import { Draft, STEP_COUNT, applyPatch, emptyDraft, stepBlocker } from './draft';
import { inviteRefusalOf } from './inviteRefusal';

/**
 * Onboarding a portal user: the journey behind « Onboarder un utilisateur ».
 *
 * Five steps where the mockup has seven, because two of its steps have no data
 * behind them and two of the remaining ones fold together:
 *
 * - the contact search stays, against **our own ingested contacts** rather than a
 *   live Salesforce call, and on the **address** rather than an External ID (PCG-84).
 *   The identity comes from the source: step 1 types no name and no function;
 * - the role and the perimeter fold into the account step: they belong to the couple
 *   (person × account), so one role for N accounts could not be expressed;
 * - the activity perimeter becomes a market scope, the only perimeter the model has;
 * - the dashboards and the partner list are steps 3 and 4, both **optional**. They
 *   were named on the recap until `assignableDashboards` and `assignablePartnerLists`
 *   gave the screen something to choose from;
 * - the referring account manager still has no query behind it. It is named on the
 *   recap rather than faked.
 *
 * Attention, steps 3 and 4 never block, and will show an empty catalogue to everybody for
 * now: neither catalogue has a writer yet. Finishing the journey without granting
 * anything is the normal outcome, not a degraded one.
 *
 * Everything is sent in **one** mutation: the mail must leave after the last refusal,
 * and a person half-attached is worse than one not created at all.
 */
const OnboardingPage = () => {
    const navigate = useNavigate();
    /**
     * L'adresse semée quand on arrive depuis « Ajouter un compte » d'une fiche existante
     * (PCG-357) : l'assistant s'ouvre avec l'e-mail pré-rempli, l'opérateur recherche le
     * contact (déjà onboardé) et n'a plus qu'à choisir le compte à ajouter. Le geste, la
     * mutation et le « déjà onboardé » sont ceux de l'onboarding : c'est le meme parcours,
     * juste amorcé. Aucun second mail n'en sort : `inviteUser` n'envoie rien a une personne
     * deja active.
     */
    const location = useLocation();
    const addAccountEmail = (location.state as { addAccountEmail?: string } | null)?.addAccountEmail ?? '';
    const addingAccount = addAccountEmail.length > 0;

    const [step, setStep] = useState(1);
    /**
     * Whether the review has been reached once. It is what makes « Modifier » a round
     * trip: in the mockup it is a one-way street, correcting the accounts sends you
     * back through every later step to reach the recap again.
     */
    const [reviewed, setReviewed] = useState(false);
    const [draft, setDraft] = useState<Draft>(() => emptyDraft(addAccountEmail));
    const [inviteUser, { data, loading, error, reset }] = useMutation(InviteUserDocument);

    /**
     * The roles, read here too: from the cache step 2 already filled, so it costs no
     * round trip.
     *
     * The footer is what has to refuse, and it outlives step 2: a role can stop being
     * grantable while the operator is on the review, where step 2 is not even mounted.
     * Passing what the step knew down to the footer would freeze it at the moment the
     * step was left, which is exactly the staleness this check exists to catch.
     */
    const { data: rolesData } = useQuery(RolesDocument);
    const roles = (rolesData?.portal ?? []).filter(Boolean);

    // Never a bare spread: correcting the address has to drop the markets the
    // previous address's suggestion had ticked, on every path. See `applyPatch`.
    const patch = (values: Partial<Draft>) => setDraft((current) => applyPatch(current, values));
    const blocker = stepBlocker(step, draft, roles);

    const goToStep = (next: number) => {
        if (next === STEP_COUNT) {
            setReviewed(true);
        }
        setStep(next);
    };

    const result = data?.inviteUser;

    const submit = async () => {
        /**
         * **Attention, no contact, no send**, and this guard is what makes the rule an
         * invariant rather than an appearance.
         *
         * `contactId` is `ID!` since PCG-85: there is no invitation to compose without
         * one, so the alternative to returning here would be an `as string` on a value
         * the type says can be null. The footer already refuses on the same fact:
         * `stepBlocker(STEP_COUNT, …)` re-runs step 1's blocker, so the button is
         * disabled and says why, and the recap says it a second time in the block the
         * operator would go and fix. This is the third statement of one rule, the one
         * that holds if the other two are ever bypassed.
         */
        if (!draft.contact) {
            return;
        }
        await inviteUser({
            variables: {
                input: {
                    email: draft.email.trim(),
                    /*
                     * **The contact picked at step 1 travels with the invitation**
                     * (PCG-85). The screen had the id under its hand since PCG-84 and
                     * dropped it on the floor; the API now takes it, confronts it with
                     * the address and the actor's portfolio, and writes the
                     * `contact_onboarding` link in the same transaction as the
                     * attachments. That link is what makes a second search of the same
                     * address answer « déjà onboardée »: a state this screen has been
                     * able to display since PCG-84 and which never occurred.
                     */
                    contactId: draft.contact.id,
                    /*
                     * The identity of the contact picked at step 1, not a keystroke.
                     * `InviteUserInput` keeps the three fields alongside the id: the id
                     * anchors the person to the source, these carry what the source says
                     * about them at the moment of the invitation.
                     *
                     * Attention, `draft.contact` is guaranteed to belong to **this** address:
                     * `applyPatch` nulls it as soon as the address changes, and step 1's
                     * blocker (re-run here by `stepBlocker(STEP_COUNT, …)`) refuses
                     * until one has been picked again. Without both, correcting the
                     * address on the review would send the invitation to the new
                     * address under the previous person's name. The API holds the same
                     * rule from its own side now, and answers CONTACT_EMAIL_MISMATCH.
                     *
                     * Null rather than an empty string: absence lets the API keep what
                     * it already holds for a person who exists, where `''` would
                     * overwrite it.
                     */
                    firstName: draft.contact.firstName,
                    lastName: draft.contact.lastName,
                    function: draft.contact.jobFunction,
                    attachments: draft.attachments.map((attachment) => ({
                        organizationId: attachment.organizationId,
                        roleId: attachment.roleId as string,
                        dataScope: attachment.dataScope,
                        // PCG-305 : des ACTIVITES et jamais un marche, meme quand l'ecran a coche
                        // un marche entier. Le marche n'est qu'une case qui en coche plusieurs.
                        needIds: attachment.needIds,
                        serviceIds: attachment.serviceIds,
                    })),
                    // Absence is absence: no row, and no third state saying « aucun ».
                    // An empty list rather than null would read the same to the API, but
                    // null is what « the operator granted nothing » actually means.
                    dashboardIds: draft.dashboards.length > 0 ? draft.dashboards.map((entry) => entry.id) : null,
                    partnerListId: draft.partnerListId,
                },
            },
        });
    };

    if (result?.userId) {
        return (
            <section className="flex max-w-2xl flex-col gap-4">
                <h1 className="text-xl font-semibold text-text-900">{c('Onboarding done').t`Onboarding recorded`}</h1>
                {/* Three outcomes, and the screen says which one. Claiming « invitation
                    envoyée » when the send failed would send the operator waiting for a
                    mail that is still in the retry queue. */}
                <Alert type={result.activationEmailSent ? 'success' : 'warning'}>
                    {result.activationEmailSent
                        ? c('Onboarding done')
                              .t`The activation email has left for ${draft.email}. The person stays « invited » until they set their password.`
                        : c('Onboarding done')
                              .t`The attachments are recorded, but no email left: either the person already had an active account, or the send failed and sits in the retry queue.`}
                </Alert>
                <div className="flex flex-wrap gap-2">
                    <Button category="default" onClick={() => navigate(`/utilisateurs/${result.userId}`)}>
                        {c('Onboarding action').t`Open the user sheet`}
                    </Button>
                    {/* **`reset()` is what actually leaves this screen.** The three
                        setters below clear the draft, but the confirmation is rendered
                        on `data?.inviteUser`, which is Apollo's and survives them: so
                        without it the button emptied the journey and stayed here,
                        re-rendering the success sentence with a now-empty address:
                        « L'e-mail d'activation est parti vers . ». The state said
                        « onboarding enregistré » about a draft that no longer existed. */}
                    <Button
                        category="weak"
                        onClick={() => {
                            reset();
                            setDraft(emptyDraft());
                            setStep(1);
                            setReviewed(false);
                        }}
                    >
                        {c('Onboarding action').t`Onboard another person`}
                    </Button>
                </div>
            </section>
        );
    }

    const steps: Step[] = [
        { name: c('Onboarding step').t`Identification`, state: step === 1 ? 'current' : 'done' },
        {
            name: c('Onboarding step').t`Accounts, roles and perimeters`,
            state: step === 2 ? 'current' : step > 2 ? 'done' : 'upcoming',
        },
        {
            name: c('Onboarding step').t`Dashboards`,
            state: step === 3 ? 'current' : step > 3 ? 'done' : 'upcoming',
        },
        {
            name: c('Onboarding step').t`Partners`,
            state: step === 4 ? 'current' : step > 4 ? 'done' : 'upcoming',
        },
        { name: c('Onboarding step').t`Review and invite`, state: step === 5 ? 'current' : 'upcoming' },
    ];

    return (
        <section className="flex flex-col gap-5">
            <header className="flex flex-wrap items-start gap-4">
                <div>
                    <h1 className="text-xl font-semibold text-text-900">
                        {addingAccount ? c('Onboarding').t`Add an account` : c('Onboarding').t`Onboard a user`}
                    </h1>
                    {addingAccount ? (
                        // On dit le geste et son innocuité pour une personne déjà active :
                        // c'est le même parcours, mais il ne recrée personne et n'envoie
                        // aucun mail à qui a déjà activé son compte.
                        <p className="mt-1 text-xs text-text-600">
                            {c('Onboarding')
                                .t`Adding an account to ${addAccountEmail}. Search this address to find them, then pick the account to add: no second person is created, and no email is sent if they already activated their account.`}
                        </p>
                    ) : (
                        <p className="mt-1 text-xs text-text-600">{c('Onboarding').t`Step ${step} of ${STEP_COUNT}`}</p>
                    )}
                </div>
                <Link to="/utilisateurs" className="ml-auto">
                    <Button category="weak">{c('Onboarding action').t`Cancel`}</Button>
                </Link>
            </header>

            <div className="grid gap-6 lg:grid-cols-[240px_1fr] lg:items-start">
                <Stepper steps={steps} caption={c('Onboarding').t`Onboarding steps`} />

                <div className="flex flex-col gap-4">
                    <div className="rounded-lg border border-border-norm bg-background-norm px-4 py-4">
                        {step === 1 && <IdentityStep draft={draft} onChange={patch} />}
                        {step === 2 && (
                            <AttachmentsStep draft={draft} onChange={patch} onEditIdentity={() => setStep(1)} />
                        )}
                        {step === 3 && <DashboardsStep draft={draft} onChange={patch} />}
                        {step === 4 && <PartnerListStep draft={draft} onChange={patch} />}
                        {step === 5 && <ReviewStep draft={draft} onEditStep={setStep} />}
                    </div>

                    {/* **These refusals name a state of the data, not a failure of
                        the screen** (PCG-85, widened at PCG-110). The single red line
                        that stood here: « l'invitation n'a pas pu être envoyée »:
                        read as « ça a planté », and « ça a planté » is answered by
                        clicking again, which is the wrong move for nearly all of them.
                        `inviteRefusalOf` carries the wording and sorts the codes into
                        four families: search the address again, one gesture elsewhere
                        that lifts it, a right or a perimeter that is not yours, and a
                        draft the screen should never have let compose. */}
                    {error && <RefusalNotice refusal={inviteRefusalOf(error)} />}

                    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border-norm bg-background-norm px-4 py-3">
                        <Button category="weak" disabled={step === 1} onClick={() => setStep(step - 1)}>
                            {c('Onboarding action').t`Previous`}
                        </Button>
                        {/* Why the button refuses, next to the button. */}
                        <span className="flex-1 text-sm text-text-600">{blocker ?? ''}</span>
                        {reviewed && step < STEP_COUNT && (
                            <Button category="weak" disabled={Boolean(blocker)} onClick={() => goToStep(STEP_COUNT)}>
                                {c('Onboarding action').t`Back to the review`}
                            </Button>
                        )}
                        {step < STEP_COUNT ? (
                            <Button category="default" disabled={Boolean(blocker)} onClick={() => goToStep(step + 1)}>
                                {c('Onboarding action').t`Continue`}
                            </Button>
                        ) : (
                            // Refused as well as explained, like every other step: the
                            // review re-runs step 2's checks, and a later edit can break
                            // them: a suggestion gone stale, or a role that stopped
                            // being grantable. A message beside a live button would let
                            // the operator send the very thing it warns about.
                            <Button category="default" loading={loading} disabled={Boolean(blocker)} onClick={submit}>
                                {c('Onboarding action').t`Send the invitation`}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default OnboardingPage;
